import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import { ListLayout } from '../../../global/tables/table-list-layout';
import { ListDayItem } from '../shared/list-day-item/list-day-item';
import { Paging } from '../../../global/_paging/paging-container';
import DefaultButton from '../../../global/default-button/default-button';
import OptionsButton from '../../../global/options-button/options-button';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import { groupListByTime, isPreviousGroupDayDifferent } from '../../../../infrastructure/utils/common.utils';
import { IRoomDetailsDTO } from '../../../../api/rooms.dao';
import { CommonModal } from '../../../global/modal/common/common-modal';
import UploadButton from '../shared/upload-button';
import { DefaultCloseModalButton } from '../../../global/modal/common/common-close-modal-buttons';
import { SearchInput } from '../../../global/search-input/search-input';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { GenerateRoomsVM } from './_generate-rooms/generate-rooms.viewmodel';
import { GenerateRoomsModal } from './_generate-rooms/_generate-rooms-modal/generate-rooms-modal.presentation';
import { RoomListItem } from './_room-list-item/room-list-item';
import { RoomsViewModel } from './rooms.viewmodel';
import { OpenFrom } from '../../../../tracking-service/tracking-values';
import { DATE_AND_TIME_TEXT, EMPTY_SEARCH_RESULT, MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: RoomsViewModel;
    generateRoomsVM: GenerateRoomsVM;
    intl: IntlShape;
    resetSearchQuery: () => void;
}

const RoomsListPresentation = injectIntl(observer((props: IProps) => {
    const {
        vm,
        generateRoomsVM,
        intl,
        resetSearchQuery
    } = props;

    const thead = [
        intl.formatMessage(DATE_AND_TIME_TEXT),
        intl.formatMessage({
            id: 'oe.room.name',
            defaultMessage: 'Room Name',
            description: 'Label text'
        }),
        intl.formatMessage({
            id: 'oe.common.presenters',
            defaultMessage: 'Presenters',
            description: 'Label text'
        }),
        intl.formatMessage({
            id: 'oe.common.operators',
            defaultMessage: 'Operators',
            description: 'Label text'
        }),
        <div className={classNames('search-input-container', {
            'no-result': (
                !vm.getRooms()?.length &&
                !!vm.getSearchQuery()
            )
        })}>
            <SearchInput
                resetSearch={resetSearchQuery}
                onChange={(e, value) => vm.setSearchQuery(value)}
                withResetSearchButton
                withDebounce
            />
        </div>
    ];

    useEffect(() => {
        vm.setFirstLoad(true);
    }, []);

    const roomsList = vm.getRooms();
    const event = vm.getEvent();
    const loading = vm.getLoading();
    const groupTimeRooms = groupListByTime(roomsList);

    const isEmpty = !roomsList?.length && !loading;

    const uploadOperatorsButtonProperties = vm.composeUploadOperatorsButtonProperties();

    const roomsOptions = [
        hasPermission(Permission.CREATE_AND_ASSIGN_ROOM) && {
            label: 'Create & Assign Rooms',
            id: 'oe.rooms.createAndAssign',
            action: generateRoomsVM.showForm,
            disabled: !vm.isPipelineReady()
        },
        {
            label: 'Download Operators',
            id: 'oe.operators.download',
            action: () => vm.downloadAndTrackOperatorsCSV(OpenFrom.Rooms)
        },
        hasPermission(Permission.UPLOAD_OPERATORS) && {
            label: uploadOperatorsButtonProperties.text,
            id: 'oe.operators.upload',
            disabled: uploadOperatorsButtonProperties.disabled,
            action: () => vm.setUploadOperatorsCSVModalOpen(true)
        }
    ].filter(Boolean);

    const isRoomEditable = hasPermission(Permission.EDIT_ROOM);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation/>
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.CREATE_ROOM) && <DefaultButton
                            onClick={() => vm.createRoom(OpenFrom.Rooms)}
                            text={intl.formatMessage({
                                id: 'oe.rooms.new',
                                defaultMessage: '{prefix} New Room',
                                description: 'Button text'
                            }, {
                                prefix: '+'
                            })}
                            modifier={['fat', 'confirm-safe']}
                        />}
                        {!!roomsOptions.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={roomsOptions}
                        />}
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout'>
                <div>
                    <ListLayout
                        thead={thead}
                        isDisabled={isEmpty}
                    >
                        {loading && <TableLoadingRow colSpan={thead.length}/>}
                        {!loading && groupTimeRooms.map((group: IRoomDetailsDTO[], groupIndex) => {
                            const prevGroup = groupTimeRooms.length && groupTimeRooms[groupIndex - 1];
                            const isDayRendered = prevGroup ? isPreviousGroupDayDifferent(group, prevGroup, event.timeZone) : false;

                            return group.length && (
                                <React.Fragment key={group[0].roomId}>
                                    {!isDayRendered &&
                                    <ListDayItem
                                        date={group[0].startDateTime}
                                        eventTimezone={event.timeZone}
                                        colSpan={thead.length}
                                    />}
                                    {group.slice().reverse().map((room: IRoomDetailsDTO, roomIndex) => (
                                        <RoomListItem
                                            {...room}
                                            onEdit={vm.updateRoom}
                                            key={room.roomId}
                                            eventTimezone={event.timeZone}
                                            isSelected={vm.isEditFormShown(room.roomId)}
                                            isFirstItemInDayGroup={!isDayRendered && roomIndex === 0}
                                            isStartEndTimeRenderedInPreviousRow={roomIndex !== 0}
                                            searchQuery={vm.getSearchQuery()}
                                            isRoomEditable={isRoomEditable}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            {!vm.getSearchQuery() ?
                                <FormattedMessage
                                    description='Room list empty text'
                                    defaultMessage='There are no rooms assigned to this event.'
                                    id='oe.rooms.emptyListText'
                                />
                                :
                                <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                            }
                        </div>
                    }
                </div>
                {!vm.getLoading() && <Paging pagingVM={vm.getPaginationVM()}/>}
                <CommonModal
                    title={intl.formatMessage({
                        id: 'oe.operators.upload',
                        defaultMessage: 'Upload Operators',
                        description: 'Label text'
                    })}
                    isOpen={vm.getUploadOperatorsCSVModalOpen()}
                    onRequestClose={() => {
                        vm.setUploadOperatorsCSVModalOpen(false);
                    }}
                    footer={(
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                text={intl.formatMessage({
                                    id: 'oe.operators.upload',
                                    defaultMessage: 'Upload Operators',
                                    description: 'Label text'
                                })}
                                modifier={['fat', 'confirm-safe']}
                                onClick={vm.uploadOperatorsCSV}
                                disabled={!vm.getOperatorsCSVFileName()}
                            />
                            <DefaultCloseModalButton/>
                        </div>
                    )}>
                    <div className='attendees-list__csv-modal-form'>
                        <UploadButton
                            fileName={vm.getOperatorsCSVFileName()}
                            onChange={vm.setOperatorsCSVFile}
                            className='attendees-list-option attendees-list-option__in-modal'
                        />
                    </div>
                </CommonModal>
                <GenerateRoomsModal vm={generateRoomsVM}/>
            </section>
        </div>
    );
}));

const RoomsListContainer = (props: { vm: RoomsViewModel }) => {
    const { vm } = props;
    const { clearSearchQuery, clearRoomsList } = vm;

    useEffect(() => {
        clearSearchQuery();

        return () => {
            clearRoomsList();
        };
    }, []);

    return <RoomsListPresentation vm={vm} generateRoomsVM={vm.generateRoomsVM}
                                  resetSearchQuery={vm.clearSearchQuery}
            />;
};

export { RoomsListContainer as RoomsList, RoomsListPresentation };
